import React, { useCallback, useContext, useState } from 'react';
import { GestureResponderEvent, StyleSheet, useWindowDimensions, View } from 'react-native';
import * as AppUtils from '@/utilities/AppUtils';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Image } from 'expo-image';
import {
  Button,
  Window
} from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { AppContext } from '@/components/AppContext';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import { ExternalPathString, Link } from 'expo-router';
import { Community, UNKNOWN_COMMUNITY } from "@/types/community";
import Bullet from '@/components/Bullet';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import Screenshots from "@/components/Screenshots";
import { CustomDivider } from '@/components/CustomDivider';
import { RabblStats } from '@/components/RabblStats';
import { SafeAreaView } from 'react-native-safe-area-context';
import { CustomAccordian, AccordianSection } from '@/components/CustomAccordian';
import { BulletList } from '@/components/BulletList';
import * as TextBlocks from '@/utilities/TextBlocks';
import { RabblLink } from '@/components/RabblLink';
import { Video } from '@/components/Video';
import { Gender } from '@/types/user_info';
import { AllHomepagesFooter } from "@/components/AllHomepagesFooter";

const styles = StyleSheet.create({
  image: {
    width: 200,
    height: 200,
  },
  centerAndPadding: {
    padding: 15,
    alignItems: 'center',
  },
  windowView: {
    alignItems: 'center',
  },
  horizontal: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  contentWrapper: {
    padding: 20,
    gap: 20,
    width: '100%',
    alignItems: 'center',
  },
  contentWrapper2: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    paddingTop: 5,
    gap: 10,
    width: '100%',
  },
  black: {
    color: '#000000',
  },
  divider1: {
    marginTop: 15,
    marginBottom: 10,
  },
  padding5: {
    padding: 5,
  },
  bold: {
    fontWeight: "bold",
  },
  strikethrough: {
    textDecorationLine: 'line-through',
  },
  empty: {
  },
  simplegap: {
    gap: 5,
    padding: 20,
    paddingTop: 0,
  },
  bulletWrapper: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  bulletsWrapper: {
    padding: 10,
    paddingTop: 0,
    gap: 15,
  },
  faqsWrapper: {
    padding: 10,
  },
  priceWrapper: {
    alignItems: "center",
    gap: 5,
  },
  priceDividerWrapper: {
    alignSelf: "center",
    width: 200,
    marginVertical: 10,
  },
  textUnderline: {
    textDecorationLine: 'underline',
  },
  freeText: {
    fontWeight: "bold",
    textDecorationLine: "underline",
    fontSize: 25,
  },
  largeText: {
    fontSize: 25,
  },
  button: {
    alignSelf: "center",
    width: 200,
  },
});

interface DashboardProps {
  community: Community,
  buttons?: () => React.ReactNode,
  joinLoginLinksButtons?: boolean,
}

const Dashboard: React.FC<DashboardProps> = ({
  buttons = undefined,
  joinLoginLinksButtons = true,
  community,
}) => {
  const community_info = useSelector((state: RootState) => state.user.current_community_info);
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const faqData = new Map<string, AccordianSection>([
    [
      "Why is there a cover charge?",
      {
        content: (
          <View style={AppConstants.STYLES.gap20}>
            <AppText>The cover charge reduces fake profiles, scammers, helps fund Rabbl, and the owner of the {community.name} Rabbl gets a cut which incentivizes them to market it and bring in more people.</AppText>
            <AppText>We tell you above how many people are in the Rabbl to help you decide if you want to join (including the break down of men and women).</AppText>
            <AppText>If you join and don't like anyone, hang in there, because you've got a lifetime membership to this Rabbl. You don't even have to check the app because we'll optionally email you when new people join matching your filter.</AppText>
            <AppText>If there's some other information we can show that will help you decide, <Link href="/contact" style={CustomTextStyles.link}>let us know</Link> and we'll look into adding it.</AppText>
          </View>
        )
      }
    ],
    [
      "What if no one's in the Rabbl that I like?",
      {
        content: (
          <>
            <AppText>Think of it like paying a club entrance fee at 7PM, and hopefully more people arrive later that you like, except in this case later might be some days or months. You have the option of us emailing you when new people join that match your filter.</AppText>
          </>
        )
      }
    ],
    [
      "Can I get a refund if I don't like it?",
      {
        content: (
          <>
            {TextBlocks.REFUNDS_VIEW}
          </>
        )
      }
    ],
    [
      "How can I pay to join a Rabbl?",
      {
        content: (
          <>
            <AppText>{AppConsts.PAYMENTS_LINE1}</AppText>
          </>
        )
      }
    ],
    [
      "What does lifetime membership mean?",
      {
        content: (
          <View style={AppConstants.STYLES.gap20}>
            <AppText>Once you've paid the one-time entrance fee to join this Rabbl, you will have a membership for the life of this Rabbl. This membership is non-transferable and your continued use of your membership is subject to Rabbl's <Link href="/terms" style={CustomTextStyles.link}>terms and conditions</Link>.</AppText>
            <AppText>A membership entitles you to creating a dating profile in this Rabbl, unlimited likes of other members in this Rabbl and unlimited messages with other members in this Rabbl. Note that membership to this Rabbl does not grant membership to other Rabbl communities. You must pay an entrance fee for every Rabbl you wish to join. Each membership is independent but you can share the same profile across memberships if you'd like (or you can create separate profiles). You may hide & show your profile at any time. The owner of this Rabbl may terminate this Rabbl at any time and your membership in this Rabbl will end at that time.</AppText>
          </View>
        )
      }
    ],
  ]);

  const isCommunityFreeMen = AppUtils.isCommunityFree(community, Gender.GENDER_MAN);
  const isCommunityFreeWomen = AppUtils.isCommunityFree(community, Gender.GENDER_WOMAN);
  const isCommunityFreeMenOrWomen = isCommunityFreeMen || isCommunityFreeWomen;
  const isCommunityFreeMenAndWomen = isCommunityFreeMen && isCommunityFreeWomen;

  return (
    <View
      style={[
        styles.simplegap,
        AppConstants.STYLES.width100P,
        {
          maxWidth: AppUtils.calculateMaxWidth(screenWidth, AppUtils.MaxWidthType.INNER2)
        }
      ]}
    >
      <BulletList
        data={[
          { content: "Date people that like " + community.name },
          { content: AppUtils.getLifetimeMembershipText(community) },
          { content: "Unlimited free likes", bold: true },
          { content: "Unlimited free messages", bold: true },
          { content: "Optional email and/or app notifications" },
          { content: "Optional notifications when people join" },
        ]}
      />

      <CustomDivider fullWidth={true} margin={true} />

      <View style={AppConstants.STYLES.alignCenter}>
        <AppText style={ [isCommunityFreeMenAndWomen ? styles.strikethrough : styles.empty, styles.largeText] }>
          <AppText style={styles.freeText}>One-time</AppText> Cover Charge
        </AppText>
      </View>

      { isCommunityFreeMenOrWomen && (
        <>
          <View style={AppConstants.STYLES.alignCenter}>
            <View style={AppConstants.STYLES.padding5} />
            <AppText style={styles.freeText}>Free {AppUtils.getFreeCommunityPromotionText(community)}</AppText>
          </View>
        </>
      )}

      <View style={AppConstants.STYLES.padding5} />

      {
        community.price_men == community.price_women ?
            (
              <>
                <View style={AppConstants.STYLES.alignCenter}>
                  {
                    joinLoginLinksButtons ?
                        <Link href={"/join"} asChild={AppUtilities.isWeb()}>
                          <CustomText type={'large'} style={ isCommunityFreeMenOrWomen ? styles.strikethrough : styles.empty }>
                            {AppUtils.formatPublicPrice(community.price_men)}
                          </CustomText>
                        </Link>
                      :
                        <CustomText type={'large'} style={ isCommunityFreeMenOrWomen ? styles.strikethrough : styles.empty }>
                          {AppUtils.formatPublicPrice(community.price_men)}
                        </CustomText>
                  }
                </View>
              </>
            )
          :
            (
              <>
                <View style={styles.priceWrapper}>
                  {
                    joinLoginLinksButtons ?
                        <>
                          <Link href={"/join"} asChild={AppUtilities.isWeb()}>
                            <CustomText type={'large'} style={ isCommunityFreeWomen ? styles.strikethrough : styles.textUnderline }>
                              Women
                            </CustomText>
                          </Link>
                          <Link href={"/join"} asChild={AppUtilities.isWeb()}>
                            <CustomText type={'large'} style={ isCommunityFreeWomen ? styles.strikethrough : styles.empty }>
                              {AppUtils.formatPublicPrice(community.price_women)}
                            </CustomText>
                          </Link>
                        </>
                      :
                        <>
                          <CustomText type={'large'} style={ isCommunityFreeWomen ? styles.strikethrough : styles.textUnderline }>
                            Women
                          </CustomText>
                          <CustomText type={'large'} style={ isCommunityFreeWomen ? styles.strikethrough : styles.empty }>
                            {AppUtils.formatPublicPrice(community.price_women)}
                          </CustomText>
                        </>
                  }
                </View>

                <View style={styles.priceDividerWrapper}>
                  <CustomDivider />
                </View>

                <View style={styles.priceWrapper}>
                  {
                    joinLoginLinksButtons ?
                        <>
                          <Link href={"/join"} asChild={AppUtilities.isWeb()}>
                            <CustomText type={'large'} style={ isCommunityFreeMen ? styles.strikethrough : styles.textUnderline }>
                              Men
                            </CustomText>
                          </Link>
                          <Link href={"/join"} asChild={AppUtilities.isWeb()}>
                            <CustomText type={'large'} style={ isCommunityFreeMen ? styles.strikethrough : styles.empty }>
                              {AppUtils.formatPublicPrice(community.price_men)}
                            </CustomText>
                          </Link>
                        </>
                      :
                        <>
                          <CustomText type={'large'} style={ isCommunityFreeMen ? styles.strikethrough : styles.textUnderline }>
                            Men
                          </CustomText>
                          <CustomText type={'large'} style={ isCommunityFreeMen ? styles.strikethrough : styles.empty }>
                            {AppUtils.formatPublicPrice(community.price_men)}
                          </CustomText>
                        </>
                  }
                </View>
              </>
            )
      }
      
      { buttons && 
          <>
            <View style={styles.padding5} />

            {buttons()}
          </>
      }

      <CustomDivider fullWidth={true} margin={true} />

      <RabblStats community={community} community_info={community_info} />

      <CustomDivider fullWidth={true} margin={true} />

      <View style={AppConstants.STYLES.alignCenter}>
        <CustomText>Frequently Asked Questions</CustomText>
      </View>

      <CustomAccordian data={faqData} maxWidth={400} />
    </View>
  );
};

type Props = {
  buttons?: () => React.ReactNode,
  joinLoginLinksButtons?: boolean,
};

export default function SubdomainHomepage({
  buttons = undefined,
  joinLoginLinksButtons = true
} : Props) {
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const community = useSelector((state: RootState) => state.user.current_community);
  const [isMainWindowVisible, setIsMainWindowVisible] = useState(true);
  const domain = useSelector((state: RootState) => state.user.domain);
  const { explode } = useContext(AppContext);
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);

  const closeMainWindow = (evt: GestureResponderEvent) => {
    explode(evt);

    setIsMainWindowVisible(false);
    setTimeout(() => {
      setIsMainWindowVisible(true);
    }, 2000);
  };

  const builtInButtons = useCallback(() => {
    if (loggedIn) {
      if (AppUtils.isValidCommunityMembership(communityMembership)) {
        return null;
      } else {
        // Logged in but don't have a community membership, so show a Join button unless told not to
        if (joinLoginLinksButtons) {
          return (
            <>
              <CustomDivider fullWidth={true} />
              <View style={styles.padding5} />
              <View style={styles.button}>
                <Link href="/join" asChild className={"nontext"}><Button size="lg"><AppText style={styles.bold}>Join</AppText></Button></Link>
              </View>
            </>
          );
        } else {
          return null;
        }
      }
    }

    return (
      <>
        <CustomDivider fullWidth={true} />
        <View style={styles.padding5} />
        <View style={styles.button}>
          <Link href="/signup" asChild className={"nontext"}><Button size="lg"><AppText style={styles.bold}>Create account</AppText></Button></Link>
        </View>
        {
          !loggedIn && (
            <>
              <View style={styles.padding5} />
              <View style={styles.button}>
                <Link href="/signin" asChild className={"nontext"}><Button size="lg">{AppConsts.EXISING_USERS_LOGIN}</Button></Link>
              </View>
            </>
          )
        }
      </>
    );
  }, [loggedIn, communityMembership, joinLoginLinksButtons]);

  //if (AppUtilities.DEBUGLEVEL.dev) {
    //AppUtilities.info("SubdomainHomepage render community dir:");
    //AppUtilities.dir(community);
  //}

  if (!community || !AppUtils.isValidCommunity(community)) {
    return (
      <View style={AppConstants.STYLES.width100P}>
        <View style={AppConstants.STYLES.container2}>
          <CustomText>Not Found</CustomText>
          <View style={AppConstants.STYLES.padding1} />
          <AppText>The community for <AppText style={CustomTextStyles.normal}> {domain}</AppText> doesn't exist or isn't approved yet.</AppText>
          <View style={AppConstants.STYLES.padding1} />
          <RabblLink key={-1} community={UNKNOWN_COMMUNITY} asChild={true} href="/findrabbl">
            <Button>Other communities</Button>
          </RabblLink>
          <View style={AppConstants.STYLES.padding1} />
          <Video
            source={"/videos/5wI1gOKm70k.mp4"}
            width={AppConsts.DEFAULT_VIDEO_WIDTH}
            height={127}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={[AppConstants.STYLES.basicScrollViewCenter, { width: "100%", maxWidth: AppUtils.calculateMaxWidth(screenWidth, AppUtils.MaxWidthType.INNER2) }]}>
      <CustomText>{community.name}</CustomText>
      <Image
        source={AppUtils.getCommunityImage(community)}
        //className="w-full h-48 rounded-xl"
        alt={community.name}
        contentFit='contain'
        style={styles.image}
      />
      <AppText>{community.details}</AppText>
      <>
        {
          community.external_link && community.external_link.length > 0 && (
            <Link href={community.external_link as ExternalPathString} target="_blank" style={CustomTextStyles.link}>{community.external_link}</Link>
          )
        }
      </>
      <CustomDivider fullWidth={true} />
      {
        isMainWindowVisible &&
          (
            <Window onClose={closeMainWindow}>
              <View style={[styles.windowView, { maxWidth: AppUtils.calculateMaxWidth(screenWidth, AppUtils.MaxWidthType.INNER) }]}>
                <View style={styles.contentWrapper}>
                  <AppText style={styles.black}>Join the <AppText style={styles.bold}>{community.name}</AppText> Rabbl</AppText>
                  <CustomDivider fullWidth={true} />
                </View>
                <Dashboard buttons={buttons ? buttons : builtInButtons} community={community} joinLoginLinksButtons={joinLoginLinksButtons} />
                { joinLoginLinksButtons && 
                    <View style={styles.contentWrapper2}>
                      {buttons ? buttons() : builtInButtons()}
                    </View>
                }
              </View>
            </Window>
        )
      }

      <View style={AppConstants.STYLES.padding10} />

      <Screenshots />

      <View style={AppConstants.STYLES.padding10} />

      <AllHomepagesFooter />
      
    </View>
  );
}
